import classNames from 'classnames';
import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import React from 'react';
import ActivityItem from './ActivityItem';
import * as styles from './CommentActivityItem.module.scss';
import SVGIcon from '../../components/SVGIcon';
import {
  truncateMentionContent,
  TRUNCATION_LENGTHS,
} from '../../editor/mentions/MentionBase';
import { getAttachmentMentionText } from '../../editor/utils/deltaToText';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';
import { PostActivityAction } from '../../graphql/generated/graphql';
import commentIcon from '../../icons/non-prefilled/16x16/comment.svg';
import commentResolvedIcon from '../../icons/non-prefilled/16x16/commentResolved.svg';
import replyIcon from '../../icons/non-prefilled/16x16/reply.svg';
import getFirstName from '../../utils/text/getFirstName';
import CommentPreview from '../comment/CommentPreview';

const COMMENT_ACTIVITY_ITEM_ACTORS_FRAGMENT = gql(`
  fragment CommentActivityItem_Actors on User {
    id
    ...ActivityItem_Actors
  }
`);

const MAX_COMMENT_LINE_COUNT = 2;

const icons = {
  [PostActivityAction.CommentCreate]: (
    <SVGIcon className={styles.commentIcon}>{commentIcon}</SVGIcon>
  ),
  [PostActivityAction.CommentReply]: (
    <SVGIcon className={styles.commentIcon}>{replyIcon}</SVGIcon>
  ),
  [PostActivityAction.CommentResolve]: (
    <SVGIcon className={styles.resolveIcon}>{commentResolvedIcon}</SVGIcon>
  ),
} as const;

export const isCommentActivity = (
  type: PostActivityAction,
): type is keyof typeof icons => Object.hasOwn(icons, type);

interface CommentActivityItemProps {
  type: keyof typeof icons;
  timestamp: string | number;
  actors: DocumentType<typeof COMMENT_ACTIVITY_ITEM_ACTORS_FRAGMENT>[];
  content: Delta | Op[];
  context: Delta | Op[];
  href?: string;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const CommentActivityItem = ({
  type,
  timestamp,
  actors,
  context,
  content,
  href,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: CommentActivityItemProps) => {
  return (
    <ActivityItem
      icon={icons[type]}
      description={
        <>
          <CommentContext
            context={context}
            quoted={type === PostActivityAction.CommentReply}
          />
          <CommentPreview
            collapseLines
            content={content}
            link={false}
            maxLines={MAX_COMMENT_LINE_COUNT}
          />
        </>
      }
      timestamp={timestamp}
      actors={actors}
      withActorsNames
      href={href}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    />
  );
};

interface CommentContextProps {
  context: Delta | Op[];
  quoted?: boolean;
}

const CommentContext = ({ context, quoted }: CommentContextProps) => {
  if (!context?.length) {
    return null;
  }

  const delta = new Delta(context);
  return (
    <div
      className={classNames(styles.commentContext, {
        [styles.isCommentQuote]: quoted,
      })}
    >
      <div className={styles.commentContextInner}>
        {delta.ops.map((op, index) => {
          const key = index;
          if (typeof op.insert === 'string') {
            return op.insert;
          }
          if (
            typeof op.insert === 'object' &&
            typeof op.insert.mention === 'object' &&
            !!op.insert.mention
          ) {
            const optionalString = (value: unknown) =>
              typeof value === 'string' ? value : undefined;

            const mentionText =
              optionalString(op.attributes?.['mention-text']) ||
              optionalString(op.attributes?.content) ||
              '';

            const isUserMention =
              'type' in op.insert.mention && op.insert.mention.type === 'user';
            const content = isUserMention
              ? `@${getFirstName(mentionText) ?? ''}`
              : `+${mentionText}`;

            return (
              <span className={styles.mention} key={key}>
                {truncateMentionContent(content, TRUNCATION_LENGTHS.comment)}
              </span>
            );
          }
          const attachmentMentionText = getAttachmentMentionText(op);
          if (attachmentMentionText) {
            return (
              <span className={styles.mention} key={key}>
                +
                {truncateMentionContent(
                  attachmentMentionText,
                  TRUNCATION_LENGTHS.comment,
                )}
              </span>
            );
          }
          if (typeof op.attributes?.content === 'string') {
            return op.attributes.content;
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default CommentActivityItem;
